"use client";

import { useState, useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PlayerAvatar } from "@/components/common/player-avatar";
import { SectionLoading } from "@/components/ui/loading";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, MessageSquare, Trophy, Clock } from "lucide-react";
import { format, isToday, isYesterday } from "date-fns";

interface Conversation {
  id: string;
  type: "direct" | "league";
  contact?: {
    id: string;
    name: string;
    profileImage?: string;
    isOnline?: boolean;
  };
  league?: {
    id: string;
    name: string;
    memberCount: number;
  };
  lastMessage?: {
    content: string;
    senderId: string;
    senderName: string;
    timestamp: number;
  };
  unreadCount: number;
  isTyping?: boolean;
  typingUsers?: string[];
}

interface ConversationListProps {
  conversations: Conversation[];
  onSelectConversation: (conversation: Conversation) => void;
  isLoading?: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  currentUserId: string;
  className?: string;
}

export function ConversationList({
  conversations,
  onSelectConversation,
  isLoading = false,
  searchQuery,
  onSearchChange,
  currentUserId,
  className,
}: ConversationListProps) {
  const [filteredConversations, setFilteredConversations] = useState<
    Conversation[]
  >([]);

  useEffect(() => {
    const filtered = conversations.filter((conversation) => {
      const searchLower = searchQuery.toLowerCase();
      if (conversation.type === "direct") {
        return (
          conversation.contact?.name.toLowerCase().includes(searchLower) ||
          conversation.lastMessage?.content.toLowerCase().includes(searchLower)
        );
      } else {
        return (
          conversation.league?.name.toLowerCase().includes(searchLower) ||
          conversation.lastMessage?.content.toLowerCase().includes(searchLower)
        );
      }
    });

    // Sort by last message timestamp (most recent first)
    filtered.sort((a, b) => {
      const aTime = a.lastMessage?.timestamp || 0;
      const bTime = b.lastMessage?.timestamp || 0;
      return bTime - aTime;
    });

    setFilteredConversations(filtered);
  }, [conversations, searchQuery]);

  const formatLastMessageTime = (timestamp: number) => {
    const date = new Date(timestamp);
    if (isToday(date)) {
      return format(date, "HH:mm");
    } else if (isYesterday(date)) {
      return "Yesterday";
    } else {
      return format(date, "MMM d");
    }
  };

  const getConversationTitle = (conversation: Conversation) => {
    if (conversation.type === "direct") {
      return conversation.contact?.name || "Unknown";
    } else {
      return conversation.league?.name || "Unknown League";
    }
  };

  const getConversationSubtitle = (conversation: Conversation) => {
    if (conversation.isTyping && conversation.typingUsers) {
      const typingText =
        conversation.typingUsers.length === 1
          ? `${conversation.typingUsers[0]} is typing...`
          : `${conversation.typingUsers.length} people are typing...`;
      return typingText;
    }

    if (!conversation.lastMessage) {
      return conversation.type === "league"
        ? "No messages yet"
        : "Start the conversation";
    }

    const { content, senderId, senderName } = conversation.lastMessage;
    const isOwnMessage = senderId === currentUserId;
    const prefix =
      conversation.type === "league" && !isOwnMessage
        ? `${senderName}: `
        : isOwnMessage
        ? "You: "
        : "";

    return `${prefix}${content}`;
  };

  const renderConversation = (conversation: Conversation) => (
    <div
      key={conversation.id}
      className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer transition-colors border-b last:border-b-0"
      onClick={() => onSelectConversation(conversation)}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="relative flex-shrink-0">
          {conversation.type === "direct" ? (
            <PlayerAvatar
              src={conversation.contact?.profileImage}
              name={conversation.contact?.name || "?"}
              size="md"
              isOnline={conversation.contact?.isOnline}
            />
          ) : (
            <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Trophy className="h-5 w-5 text-primary" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-medium text-sm truncate">
              {getConversationTitle(conversation)}
            </p>
            {conversation.type === "league" && (
              <Badge variant="outline" className="text-xs flex-shrink-0">
                {conversation.league?.memberCount} members
              </Badge>
            )}
          </div>
          <p
            className={`text-xs truncate ${
              conversation.isTyping
                ? "text-primary font-medium"
                : "text-muted-foreground"
            }`}
          >
            {getConversationSubtitle(conversation)}
          </p>
        </div>
      </div>

      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {conversation.lastMessage && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>
              {formatLastMessageTime(conversation.lastMessage.timestamp)}
            </span>
          </div>
        )}

        {conversation.unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="h-5 min-w-[20px] text-xs px-1.5 rounded-full"
          >
            {conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}
          </Badge>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className={`p-4 ${className || ""}`}>
        <SectionLoading text="Loading conversations..." minHeight="128px" />
      </div>
    );
  }

  const hasConversations = conversations.length > 0;

  return (
    <div className={`h-full flex flex-col ${className || ""}`}>
      {/* Only show search if there are conversations to search */}
      {hasConversations && (
        <div className="p-4 border-b flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      )}

      {/* Conversations List */}
      <ScrollArea className="flex-1">
        {filteredConversations.length > 0 ? (
          <div>{filteredConversations.map(renderConversation)}</div>
        ) : (
          <div className="flex items-center justify-center py-16 px-4">
            <div className="text-center">
              {searchQuery ? (
                <>
                  <Search className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <h3 className="font-medium text-sm mb-1">No results found</h3>
                  <p className="text-xs text-muted-foreground">
                    Try a different search term
                  </p>
                </>
              ) : (
                <>
                  <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <h3 className="font-medium text-sm mb-1">
                    No conversations yet
                  </h3>
                  <p className="text-xs text-muted-foreground max-w-[200px] mx-auto">
                    Switch to "People" to find someone to chat with
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
